/**
 * Dados da tela inicial do XML Scan, só a partir de /xml-scan/history.
 * Regras vindas dos dados reais (projeto Automec, 2026-11-04):
 * - totalXmls = conformes + parciais + naoConformes + errors, exato. Quem tem erro
 *   não foi lido, e a API não informa o motivo;
 * - a conformidade é um funil: não conforme = sem CST e cClassTrib; parcial = com
 *   eles, sem os valores; conforme = com todos os campos de IBS/CBS;
 * - lotes repetidos existem (mesmos números e fornecedores). Por isso a tela NÃO
 *   soma análises: o resumo é de uma análise por vez, a lida mais recente por padrão;
 * - lotes com todos os XMLs em erro vêm com status "completed";
 * - documentos anteriores a 2026 não têm campos cobrados: os percentuais por campo
 *   usam só os XMLs de 2026 em diante;
 * - `documentos` de cada fornecedor é uma amostra, não a lista completa;
 * - `scoreConformidade` tem conta própria; a tela usa conformes ÷ XMLs lidos.
 */

export const FIELD_GROUPS = [
  {
    key: "id",
    label: "Identificação",
    hint: "O que a nota declara",
    fields: [
      { field: "CST", label: "CST", desc: "Código de Situação Tributária do IBS e da CBS" },
      { field: "cClassTrib", label: "cClassTrib", desc: "Classificação tributária do item" },
    ],
  },
  {
    key: "values",
    label: "Base e valores",
    hint: "O cálculo de IBS e CBS",
    fields: [
      { field: "vBC", label: "vBC", desc: "Base de cálculo" },
      { field: "pIBSUF", label: "pIBSUF", desc: "Alíquota estadual do IBS" },
      { field: "vIBSUF", label: "vIBSUF", desc: "Valor do IBS estadual" },
      { field: "pIBSMun", label: "pIBSMun", desc: "Alíquota municipal do IBS" },
      { field: "vIBSMun", label: "vIBSMun", desc: "Valor do IBS municipal" },
      { field: "vIBS", label: "vIBS", desc: "Valor total do IBS" },
      { field: "pCBS", label: "pCBS", desc: "Alíquota da CBS" },
      { field: "vCBS", label: "vCBS", desc: "Valor da CBS" },
    ],
  },
  {
    key: "is",
    label: "Imposto Seletivo",
    hint: "Campos opcionais",
    optional: true,
    fields: [
      { field: "CSTIS", label: "CST", desc: "Situação tributária do Imposto Seletivo" },
      { field: "cClassTribIS", label: "cClassTrib", desc: "Classificação do Imposto Seletivo" },
      { field: "vBCIS", label: "vBC", desc: "Base de cálculo do Imposto Seletivo" },
      { field: "pIS", label: "pIS", desc: "Alíquota do Imposto Seletivo" },
      { field: "vIS", label: "vIS", desc: "Valor do Imposto Seletivo" },
    ],
  },
];

// Nomes da tabela de CST de IBS/CBS; um código fora daqui aparece só como código
export const CST_NAMES = {
  "000": "Tributação integral",
  "010": "Alíquotas uniformes",
  "011": "Alíquotas uniformes reduzidas",
  200: "Alíquota reduzida",
  210: "Redução de alíquota e de base",
  220: "Alíquota fixa",
  221: "Alíquota fixa proporcional",
  400: "Isenção",
  410: "Imunidade e não incidência",
  510: "Diferimento",
  515: "Diferimento com redução",
  550: "Suspensão",
  620: "Tributação monofásica",
  800: "Transferência de crédito",
  810: "Ajustes",
  820: "Regime específico",
};

const FIN_NAMES = { 1: "Normal", 2: "Complementar", 3: "Ajuste", 4: "Devolução" };

export const SUPPLIER_BANDS = [
  { key: "full", label: "Todos conformes", test: (p) => p >= 99.95, color: "#34c38f" },
  { key: "high", label: "90% a 99%", test: (p) => p >= 90, color: "#9adbbf" },
  { key: "mid", label: "50% a 89%", test: (p) => p >= 50, color: "#f2c27a" },
  { key: "low", label: "Abaixo de 50%", test: (p) => p > 0, color: "#f2a594" },
  { key: "none", label: "Nenhum conforme", test: () => true, color: "#e8776c" },
];

export const supplierBandKey = (pct) => SUPPLIER_BANDS.find((x) => x.test(pct)).key;

const add = (map, obj) => {
  Object.entries(obj || {}).forEach(([k, v]) => { map[k] = (map[k] || 0) + (Number(v) || 0); });
  return map;
};

const normalizeBatch = (b) => {
  const conformes = b.conformes || 0;
  const parciais = b.parciais || 0;
  const naoConformes = b.naoConformes || 0;
  const totalXmls = b.totalXmls || 0;
  const read = conformes + parciais + naoConformes;
  return {
    ...b,
    raw: b,
    conformes,
    parciais,
    naoConformes,
    totalXmls,
    read,
    unread: Math.max(0, totalXmls - read),
    pct: read > 0 ? (conformes / read) * 100 : 0,
    failed: read === 0,
    suppliers: Array.isArray(b.suppliers) ? b.suppliers : [],
    time: Date.parse(b.date) || 0,
  };
};

export const batchDate = (b, withTime = true) => {
  if (!b.time) return "Sem data";
  return new Date(b.time).toLocaleString("pt-BR", withTime
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" });
};

export function buildFocus(b) {
  const missing = b.missingFieldsStats || {};
  const periods = Array.isArray(b.periods) ? b.periods : [];
  const required = periods.filter((p) => p.ano >= 2026).reduce((s, p) => s + (p.totalXmls || 0), 0) || b.read;
  const exempt = periods.filter((p) => p.ano < 2026).reduce((s, p) => s + (p.totalXmls || 0), 0);

  const groups = FIELD_GROUPS.map((g) => {
    const fields = g.fields.map((f) => {
      const absent = missing[f.field] || 0;
      return { ...f, absent, pct: required > 0 ? Math.max(0, ((required - absent) / required) * 100) : 0 };
    });
    return { ...g, fields, pct: Math.min(...fields.map((f) => f.pct)) };
  });

  const withIdentification = b.read - (missing.CST || 0);
  const funnel = [
    { key: "received", label: "XMLs recebidos", value: b.totalXmls },
    { key: "read", label: "Lidos", value: b.read, drop: b.unread, dropLabel: "não lidos" },
    { key: "id", label: "Com CST e cClassTrib", value: Math.max(0, withIdentification), drop: b.naoConformes, dropLabel: "sem o grupo IBS/CBS" },
    { key: "full", label: "Com todos os valores", value: b.conformes, drop: b.parciais, dropLabel: "sem base e valores" },
  ];

  const cst = {};
  const classes = {};
  const finalities = {};
  let parcial410 = 0;
  const suppliers = b.suppliers.map((s) => {
    add(cst, s.cstUsados);
    add(classes, s.cClassTribUsados);
    add(finalities, s.finNFeDistribuicao);
    parcial410 += Math.min(s.parciais || 0, s.cstUsados?.["410"] || 0);
    const total = s.totalXmls || 0;
    const conformes = s.conformes || 0;
    return {
      raw: s,
      key: `${s.id}-${s.cnpj}`,
      name: s.nome || "Sem nome",
      cnpj: s.cnpj || "",
      total,
      conformes,
      parciais: s.parciais || 0,
      naoConformes: s.naoConformes || 0,
      pct: total > 0 ? (conformes / total) * 100 : 0,
    };
  });

  const toRows = (map) => {
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map)
      .map(([code, value]) => ({ code, value, share: total > 0 ? (value / total) * 100 : 0 }))
      .sort((p, q) => q.value - p.value);
  };

  const rank = (key) => {
    const list = suppliers.filter((s) => s[key] > 0).sort((p, q) => q[key] - p[key]);
    const total = list.reduce((s, x) => s + x[key], 0);
    const top = list.slice(0, 6);
    const topSum = top.reduce((s, x) => s + x[key], 0);
    return { key, total, count: list.length, top, share: total > 0 ? (topSum / total) * 100 : 0, rest: total - topSum };
  };

  const bands = SUPPLIER_BANDS.map((band) => {
    const list = suppliers.filter((s) => supplierBandKey(s.pct) === band.key);
    return { ...band, count: list.length, xmls: list.reduce((s, x) => s + x.total, 0) };
  });

  return {
    batch: b,
    required,
    exempt,
    groups,
    funnel,
    cst: toRows(cst).map((r) => ({ ...r, name: CST_NAMES[r.code] || `CST ${r.code}` })),
    classes: toRows(classes),
    finalities: toRows(finalities).map((r) => ({ ...r, name: FIN_NAMES[r.code] || `Finalidade ${r.code}` })),
    parcial410,
    supplierCount: suppliers.length,
    suppliers,
    rankings: { naoConformes: rank("naoConformes"), parciais: rank("parciais") },
    bands,
  };
}

// Código de UF da chave de acesso (IBGE)
const UF_CODES = {
  11: ["RO", "Rondônia"], 12: ["AC", "Acre"], 13: ["AM", "Amazonas"], 14: ["RR", "Roraima"], 15: ["PA", "Pará"], 16: ["AP", "Amapá"], 17: ["TO", "Tocantins"],
  21: ["MA", "Maranhão"], 22: ["PI", "Piauí"], 23: ["CE", "Ceará"], 24: ["RN", "Rio Grande do Norte"], 25: ["PB", "Paraíba"], 26: ["PE", "Pernambuco"], 27: ["AL", "Alagoas"], 28: ["SE", "Sergipe"], 29: ["BA", "Bahia"],
  31: ["MG", "Minas Gerais"], 32: ["ES", "Espírito Santo"], 33: ["RJ", "Rio de Janeiro"], 35: ["SP", "São Paulo"],
  41: ["PR", "Paraná"], 42: ["SC", "Santa Catarina"], 43: ["RS", "Rio Grande do Sul"],
  50: ["MS", "Mato Grosso do Sul"], 51: ["MT", "Mato Grosso"], 52: ["GO", "Goiás"], 53: ["DF", "Distrito Federal"],
};

const SIZE_BANDS = [
  { key: "s1", label: "1 XML", short: "1", test: (s) => s.total <= 1 },
  { key: "s2", label: "2 a 5 XMLs", short: "2 a 5", test: (s) => s.total >= 2 && s.total <= 5 },
  { key: "s3", label: "6 a 20 XMLs", short: "6 a 20", test: (s) => s.total >= 6 && s.total <= 20 },
  { key: "s4", label: "21 a 100 XMLs", short: "21 a 100", test: (s) => s.total >= 21 && s.total <= 100 },
  { key: "s5", label: "101 a 500 XMLs", short: "101 a 500", test: (s) => s.total >= 101 && s.total <= 500 },
  { key: "s6", label: "Mais de 500 XMLs", short: "500+", test: (s) => s.total > 500 },
];

const cstKeys = (s) => Object.keys(s.raw.cstUsados || {});
const REGIMES = [
  { key: "none", label: "Sem CST", test: (s) => cstKeys(s).length === 0 },
  { key: "integral", label: "Só tributação integral", test: (s) => { const k = cstKeys(s); return k.length === 1 && k[0] === "000"; } },
  { key: "mixed", label: "Integral e imunidade", test: (s) => { const k = cstKeys(s); return k.includes("000") && k.includes("410") && k.length === 2; } },
  { key: "immune", label: "Só imunidade", test: (s) => { const k = cstKeys(s); return k.length === 1 && k[0] === "410"; } },
];

// Aba Fornecedores: prontidão, porte, regime e estado da base
export function buildSupplierStats(suppliers) {
  const totalXmls = suppliers.reduce((sum, s) => sum + s.total, 0);
  const totalCount = suppliers.length;
  const xmlShare = (v) => (totalXmls > 0 ? (v / totalXmls) * 100 : 0);
  const share = (v) => (totalCount > 0 ? (v / totalCount) * 100 : 0);
  const sumXmls = (list) => list.reduce((sum, s) => sum + s.total, 0);

  const isFull = (s) => supplierBandKey(s.pct) === "full";
  const isNone = (s) => supplierBandKey(s.pct) === "none";
  const ready = [
    { key: "full", label: "Todos conformes", test: isFull },
    { key: "partial", label: "Em adaptação", test: (s) => !isFull(s) && !isNone(s) },
    { key: "none", label: "Nenhum conforme", test: isNone },
  ].map((r) => {
    const list = suppliers.filter(r.test);
    return { ...r, count: list.length, share: share(list.length), xmls: sumXmls(list), xmlShare: xmlShare(sumXmls(list)) };
  });

  const sizes = SIZE_BANDS.map((b) => {
    const list = suppliers.filter(b.test);
    return {
      ...b,
      count: list.length,
      xmls: sumXmls(list),
      xmlShare: xmlShare(sumXmls(list)),
      full: list.filter(isFull).length,
      none: list.filter(isNone).length,
      partial: list.filter((s) => !isFull(s) && !isNone(s)).length,
    };
  });
  const tailBands = sizes.slice(0, 2);
  const tail = {
    count: tailBands.reduce((sum, b) => sum + b.count, 0),
    none: tailBands.reduce((sum, b) => sum + b.none, 0),
    xmlShare: tailBands.reduce((sum, b) => sum + b.xmlShare, 0),
  };

  const known = REGIMES.map((r) => {
    const list = suppliers.filter(r.test);
    return { ...r, count: list.length, share: share(list.length), xmls: sumXmls(list) };
  });
  const isKnown = (s) => REGIMES.some((r) => r.test(s));
  const others = suppliers.filter((s) => !isKnown(s));
  const regimes = [...known, { key: "other", label: "Outros CST", test: (s) => !isKnown(s), count: others.length, share: share(others.length), xmls: sumXmls(others) }]
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);

  const ufOf = (s) => { const doc = (s.raw.documentos || []).find((d) => d.chave); return doc ? String(doc.chave).slice(0, 2) : ""; };
  const stateMap = new Map();
  suppliers.forEach((s) => {
    const code = ufOf(s);
    if (!UF_CODES[code]) return;
    const row = stateMap.get(code) || { code, sigla: UF_CODES[code][0], name: UF_CODES[code][1], count: 0, xmls: 0, conformes: 0, test: (x) => ufOf(x) === code };
    row.count += 1;
    row.xmls += s.total;
    row.conformes += s.conformes;
    stateMap.set(code, row);
  });
  const states = [...stateMap.values()]
    .map((u) => ({ ...u, pct: u.xmls > 0 ? (u.conformes / u.xmls) * 100 : 0 }))
    .sort((a, b) => b.count - a.count || b.xmls - a.xmls);

  const byVolume = [...suppliers].sort((a, b) => b.total - a.total);
  const top = byVolume.slice(0, 8);
  const middle = Math.floor((byVolume.length - 1) / 2);
  const medianXmls = byVolume.length % 2 ? byVolume[middle]?.total || 0 : ((byVolume[middle]?.total || 0) + (byVolume[middle + 1]?.total || 0)) / 2;
  let acc = 0;
  let forEighty = 0;
  const concentration = byVolume.slice(0, 60).map((s) => { acc += s.total; return acc; });
  let run = 0;
  for (let i = 0; i < byVolume.length; i += 1) {
    run += byVolume[i].total;
    forEighty = i + 1;
    if (run / Math.max(1, totalXmls) >= 0.8) break;
  }

  const noneList = byVolume.filter(isNone);
  const priority = noneList.slice(0, 5).map((s) => ({ ...s, ufName: UF_CODES[ufOf(s)]?.[1] || "" }));

  const roots = new Set(suppliers.map((s) => { const d = String(s.cnpj).replace(/\D/g, ""); return d.length === 14 ? d.slice(0, 8) : s.key; }));

  return {
    count: totalCount,
    groups: roots.size,
    ready,
    sizes,
    tail,
    regimes,
    states,
    top,
    topShare: xmlShare(sumXmls(top)),
    medianXmls,
    averageXmls: totalCount > 0 ? totalXmls / totalCount : 0,
    singleXml: suppliers.filter((x) => x.total === 1).length,
    forEighty,
    eightyShare: share(forEighty),
    concentration,
    priority,
    priorityShare: sumXmls(noneList) > 0 ? (sumXmls(priority) / sumXmls(noneList)) * 100 : 0,
  };
}

// Tela de uma análise: o que trava a adequação e por onde começar
export function buildBatchDetail(rawBatch, rawBatches = []) {
  const home = buildXmlScanHome(rawBatches);
  const batch = home.batches.find((x) => x.id === rawBatch.id) || normalizeBatch(rawBatch);
  const focus = buildFocus(batch);

  // Simulação: fornecedores com mais XMLs pendentes passam a ficar conformes
  const pending = focus.suppliers
    .map((s) => ({ ...s, pending: s.parciais + s.naoConformes }))
    .filter((s) => s.pending > 0)
    .sort((a, b) => b.pending - a.pending);
  const steps = [5, 10, 20, 50].filter((n) => n < pending.length).map((count) => {
    const xmls = pending.slice(0, count).reduce((sum, s) => sum + s.pending, 0);
    const reached = batch.conformes + xmls;
    return { count, xmls, pct: batch.read > 0 ? (reached / batch.read) * 100 : 0, left: Math.max(0, batch.read - reached) };
  });

  const withCritical = focus.suppliers
    .map((s) => ({ ...s, critical: s.raw.validationSummary?.CRITICAL || 0 }))
    .filter((s) => s.critical > 0)
    .sort((a, b) => b.critical - a.critical);
  const criticalTotal = batch.validationSummary?.CRITICAL || withCritical.reduce((sum, s) => sum + s.critical, 0);
  const criticalSuppliers = withCritical.slice(0, 5);

  // Amostra que a API envia por fornecedor, dos que têm mais problemas primeiro
  const seen = new Set();
  const order = { nao_conforme: 0, parcial: 1, conforme: 2 };
  const documents = focus.suppliers.flatMap((s) => (s.raw.documentos || []).map((doc) => {
    const parsed = doc.dhEmi ? Date.parse(doc.dhEmi) : NaN;
    return {
      chave: doc.chave || "",
      numero: String(doc.numero || ""),
      status: doc.conformidade || "",
      date: Number.isNaN(parsed) ? null : new Date(parsed),
      time: Number.isNaN(parsed) ? 0 : parsed,
      missing: Array.isArray(doc.missingFields) ? doc.missingFields : [],
      issues: Array.isArray(doc.validationIssues) ? doc.validationIssues : [],
      supplierName: s.name,
      supplierCnpj: s.cnpj,
    };
  })).filter((doc) => { if (!doc.chave || seen.has(doc.chave)) return false; seen.add(doc.chave); return true; })
    .sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3) || b.time - a.time);

  const previous = home.batches.find((x) => !x.failed && !x.repeated && x.time < batch.time && x.id !== batch.id) || null;

  // Grupos empresariais: CNPJs com a mesma raiz somados
  const groupMap = new Map();
  focus.suppliers.forEach((s) => {
    const digits = String(s.cnpj).replace(/\D/g, "");
    const key = digits.length === 14 ? digits.slice(0, 8) : s.key;
    const g = groupMap.get(key) || { key, name: s.name, largest: 0, cnpjs: 0, conformes: 0, parciais: 0, naoConformes: 0, total: 0 };
    if (s.total > g.largest) { g.largest = s.total; g.name = s.name; }
    g.cnpjs += 1;
    g.conformes += s.conformes;
    g.parciais += s.parciais;
    g.naoConformes += s.naoConformes;
    g.total += s.total;
    groupMap.set(key, g);
  });
  const allGroups = [...groupMap.values()].sort((a, b) => b.total - a.total);
  const groups = allGroups.slice(0, 12);

  // Forma de cada problema entre os fornecedores, do maior ao menor. A raiz quadrada
  // evita que os maiores achatem o restante em uma linha reta
  const curve = (key, list = focus.suppliers) => list.map((s) => s[key] || 0).filter((v) => v > 0).sort((a, b) => b - a).slice(0, 60).map(Math.sqrt);

  const byVolume = [...focus.suppliers].sort((a, b) => b.total - a.total);
  const volumeTop = byVolume.slice(0, 5);

  return {
    batch,
    focus,
    steps,
    pendingCount: pending.length,
    criticalTotal,
    criticalCount: withCritical.length,
    criticalSuppliers,
    criticalShare: criticalTotal > 0 ? (criticalSuppliers.reduce((sum, s) => sum + s.critical, 0) / criticalTotal) * 100 : 0,
    documents,
    years: (Array.isArray(batch.periods) ? batch.periods : []).filter((p) => p.totalXmls > 0).sort((a, b) => b.ano - a.ano),
    previous,
    delta: previous ? batch.pct - previous.pct : null,
    groups,
    groupCount: allGroups.length,
    groupsShare: batch.read > 0 ? (groups.reduce((sum, g) => sum + g.total, 0) / batch.read) * 100 : 0,
    pendingXmls: batch.parciais + batch.naoConformes,
    sparks: {
      conformes: curve("conformes"),
      parciais: curve("parciais"),
      naoConformes: curve("naoConformes"),
      critical: curve("critical", withCritical),
    },
    volumeTop,
    volumeShare: batch.read > 0 ? (volumeTop.reduce((sum, s) => sum + s.total, 0) / batch.read) * 100 : 0,
  };
}

export function buildXmlScanHome(rawBatches) {
  const batches = (rawBatches || []).map(normalizeBatch).sort((a, b) => b.time - a.time);

  // Mesmos números e campos: a análise mais antiga é apontada como repetida
  const seen = new Map();
  [...batches].reverse().forEach((b) => {
    if (b.failed) return;
    const print = JSON.stringify([b.totalXmls, b.conformes, b.parciais, b.naoConformes, b.missingFieldsStats, b.suppliers.length]);
    if (seen.has(print)) seen.get(print).repeated = true;
    seen.set(print, b);
  });

  const recentFailed = [];
  for (let i = 0; i < batches.length && batches[i].failed; i += 1) recentFailed.push(batches[i]);

  return {
    batches,
    // Análises que dá para abrir no resumo: lidas e sem repetição, da mais recente à mais antiga
    selectable: batches.filter((b) => !b.failed && !b.repeated),
    recentFailed,
    latest: batches[0] || null,
  };
}
